/**
 * Portal-specific payment method orchestrator.
 *
 * Reuses PaymentMethodForm from libs/web-ui for the form rendering and
 * tokenization services from libs/core for client-side card tokenization.
 * All API calls go through portal endpoints — no CRM services are imported.
 *
 * This is the portal equivalent of AddPaymentMethodPanel in libs/web-ui,
 * but without the CRM coupling (no ClientQueryService, PaymentMethodQueryService,
 * or ApiService calls). Config and submission go through portalPaymentService.
 */

import {
	AdyenFrontendService,
	AuthorizeNetFrontendService,
	CyberSourceFrontendService,
	detectCreditCardSubtype,
	NmiFrontendService,
	PaymentMethodSubtypeEnum,
	PaymentMethodTypeEnum,
	type PaymentGatewayConfig,
	type PaymentMethodFormData,
} from '@supply-mover-2-0/core';
import type {
	PortalCardFundingType,
	PortalCreatePaymentMethodDto,
} from '@supply-mover-2-0/shared';
import {
	encodeMockBankToken,
	encodeMockCardToken,
} from '@supply-mover-2-0/shared';
import {
	PaymentMethodForm,
	useTheme,
	useThemeColors,
	useTranslation,
	type PaymentMethodFormSupportedMethod,
} from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPaymentService from '../services/portalPaymentService';

interface PortalAddPaymentMethodProps {
	onSuccess: () => void;
	onCancel: () => void;
	// Fee may be null (portal checkout hides fees for hide-pricing buyers,
	// PD-14239); only the method type is read here.
	supportedPaymentMethods?: Array<PaymentMethodFormSupportedMethod>;
	/** Pre-select a specific payment type (e.g. ACH when adding from ACH section) */
	preSelectedPaymentType?: PaymentMethodTypeEnum;
}

export const PortalAddPaymentMethod: React.FC<PortalAddPaymentMethodProps> = ({
	onSuccess,
	onCancel,
	supportedPaymentMethods,
	preSelectedPaymentType,
}) => {
	const { t } = useTranslation();
	const { token, profile } = usePortalAuth();
	// PD-15014 — the hosted gateway fields (iframes) render their own text and
	// know nothing about the page around them. Without an explicit color they
	// fall back to dark-on-transparent, which is unreadable on a dark-branded
	// storefront. Resolve the active theme's text colors and hand them to
	// every hosted-field mount below, the same way the CRM panel does.
	// The values mirror the storefront's NATIVE input styling (index.css
	// base rule: text-gray-900/dark:text-gray-100, placeholder-gray-600/
	// dark:placeholder-gray-300) so hosted and native fields in the same
	// form read as one control style — gray-400 placeholders measured
	// under WCAG AA on brand-tinted inputs (PD-14409/PD-14603).
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const hostedFieldPlaceholderColor = isDark ? '#D1D5DB' : '#4B5563';

	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [processorType, setProcessorType] = useState<string | null>(null);
	const [gatewayConfig, setGatewayConfig] =
		useState<PaymentGatewayConfig | null>(null);
	const [secureCardFieldsCreated, setSecureCardFieldsCreated] = useState(false);
	const [secureACHFieldsCreated, setSecureACHFieldsCreated] = useState(false);
	/**
	 * PD-16381 — what the Adyen card-number field has learned so far: the
	 * network (highlighted in the accepted-brands strip) and the BIN, from
	 * which the funding type is looked up before the card is saved.
	 */
	const [detectedCardBrand, setDetectedCardBrand] = useState<string | null>(
		null,
	);
	const [cardBin, setCardBin] = useState<string | null>(null);
	const [cardFunding, setCardFunding] = useState<PortalCardFundingType | null>(
		null,
	);

	const authNetServiceRef = useRef<AuthorizeNetFrontendService | null>(null);
	const cyberSourceServiceRef = useRef<CyberSourceFrontendService | null>(null);
	const nmiServiceRef = useRef<NmiFrontendService | null>(null);
	const adyenServiceRef = useRef<AdyenFrontendService | null>(null);

	// Race condition protection: track initialization state and abort signals
	const cardInitializingRef = useRef(false);
	const achInitializingRef = useRef(false);
	const abortCounterRef = useRef(0);

	// Fetch gateway config on mount
	useEffect(() => {
		const fetchConfig = async (): Promise<void> => {
			if (token == null) {
				return;
			}

			try {
				const response = await portalPaymentService.getGatewayConfig(token);

				if (response.success && response.data != null) {
					const config: PaymentGatewayConfig = {
						processorType: response.data
							.processorType as PaymentGatewayConfig['processorType'],
						environment: response.data
							.environment as PaymentGatewayConfig['environment'],
						publicCredentials: response.data
							.publicCredentials as PaymentGatewayConfig['publicCredentials'],
						// PD-15878 — the brands the form may advertise. Copied across
						// explicitly: this builds a NEW object rather than spreading the
						// response, so anything omitted here is silently dropped.
						acceptedCardBrands: response.data.acceptedCardBrands,
					};

					setGatewayConfig(config);
					setProcessorType(config.processorType);

					// Pre-load payment processor scripts for faster form initialization
					if (config.processorType === 'cybersource') {
						CyberSourceFrontendService.preloadScript();
					} else if (config.processorType === 'nmi') {
						const tokenizationKey = config.publicCredentials?.tokenizationKey;
						if (tokenizationKey != null && tokenizationKey !== '') {
							NmiFrontendService.preloadScript(
								tokenizationKey,
								config.environment,
							);
						}
					} else if (config.processorType === 'adyen') {
						AdyenFrontendService.preloadScript(config.environment);
					}
				}
			} catch (err) {
				console.error('Failed to fetch payment gateway config:', err);
				setError(
					t('Unable to load payment configuration. Please try again later.'),
				);
			} finally {
				setLoading(false);
			}
		};

		void fetchConfig();
	}, [token, t]);

	// Handler for when payment type changes in the form
	const handlePaymentTypeChange = useCallback(
		(newPaymentType: PaymentMethodTypeEnum) => {
			// Increment abort counter to cancel any in-flight initialization
			abortCounterRef.current += 1;

			// Reset initialization flags
			cardInitializingRef.current = false;
			achInitializingRef.current = false;

			// The card fields are about to be torn down and rebuilt, so
			// nothing they reported about the last card still applies.
			setDetectedCardBrand(null);
			setCardBin(null);

			// Reset field created flags based on new type
			if (newPaymentType === PaymentMethodTypeEnum.CREDIT_CARD) {
				setSecureACHFieldsCreated(false);
			} else if (newPaymentType === PaymentMethodTypeEnum.ACH) {
				setSecureCardFieldsCreated(false);
			}

			// Cleanup and recreate hosted field services to get fresh state
			if (cyberSourceServiceRef.current != null) {
				cyberSourceServiceRef.current.cleanup();
				cyberSourceServiceRef.current = null;
			}

			if (nmiServiceRef.current != null) {
				nmiServiceRef.current.cleanup();
				nmiServiceRef.current = null;
			}

			if (adyenServiceRef.current != null) {
				adyenServiceRef.current.cleanup();
				adyenServiceRef.current = null;
			}

			setError(null);
		},
		[],
	);

	// Cleanup services on unmount
	useEffect(
		() => () => {
			abortCounterRef.current += 1;

			if (authNetServiceRef.current != null) {
				authNetServiceRef.current.cleanup();
				authNetServiceRef.current = null;
			}
			if (cyberSourceServiceRef.current != null) {
				cyberSourceServiceRef.current.cleanup();
				cyberSourceServiceRef.current = null;
			}
			if (nmiServiceRef.current != null) {
				nmiServiceRef.current.cleanup();
				nmiServiceRef.current = null;
			}
			if (adyenServiceRef.current != null) {
				adyenServiceRef.current.cleanup();
				adyenServiceRef.current = null;
			}
		},
		[],
	);

	// PD-16381 — look up whether the card is credit, debit or prepaid once its
	// BIN is known. The field reports the BIN only when it changes; the short
	// debounce collapses the 6-digit then 8-digit reports of one fast typist
	// into one request. A stale answer (the BIN changed again while it was in
	// flight) is dropped, and a failure simply leaves the label off — the
	// lookup is informational and never blocks saving the card.
	useEffect(() => {
		setCardFunding(null);

		if (cardBin == null || token == null || processorType !== 'adyen') {
			return undefined;
		}

		let cancelled = false;
		const timer = window.setTimeout(() => {
			portalPaymentService
				.getCardFunding(token, cardBin)
				.then((response) => {
					if (!cancelled) {
						setCardFunding(response.data.funding);
					}
				})
				.catch(() => undefined);
		}, 400);

		return (): void => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [cardBin, token, processorType]);

	// Callback when secure card field containers are ready (CyberSource or NMI)
	const handleSecureFieldsReady = useCallback(
		async (
			cardNumberContainerId: string,
			securityCodeContainerId: string,
		): Promise<void> => {
			if (
				secureCardFieldsCreated ||
				cardInitializingRef.current ||
				gatewayConfig == null
			) {
				return;
			}

			const currentAbortCounter = abortCounterRef.current;
			cardInitializingRef.current = true;

			try {
				if (processorType === 'cybersource') {
					if (cyberSourceServiceRef.current == null) {
						cyberSourceServiceRef.current = new CyberSourceFrontendService();
					}

					await cyberSourceServiceRef.current.initialize('CARD', gatewayConfig);

					if (currentAbortCounter !== abortCounterRef.current) {
						return;
					}

					const cardContainer = document.getElementById(cardNumberContainerId);
					const cvvContainer = document.getElementById(securityCodeContainerId);

					if (
						cardContainer == null ||
						cvvContainer == null ||
						cyberSourceServiceRef.current == null
					) {
						return;
					}

					cyberSourceServiceRef.current.createSecureCardFields(
						cardNumberContainerId,
						securityCodeContainerId,
						{
							textColor: colors.values.textPrimary,
							placeholderColor: hostedFieldPlaceholderColor,
						},
					);
				} else if (processorType === 'nmi') {
					if (nmiServiceRef.current == null) {
						nmiServiceRef.current = new NmiFrontendService();
					}

					await nmiServiceRef.current.initialize('CARD', gatewayConfig);

					if (currentAbortCounter !== abortCounterRef.current) {
						return;
					}

					const cardContainer = document.getElementById(cardNumberContainerId);
					const cvvContainer = document.getElementById(securityCodeContainerId);
					const expContainerId = 'nmi-expiration';
					const expContainer = document.getElementById(expContainerId);

					if (
						cardContainer == null ||
						cvvContainer == null ||
						expContainer == null ||
						nmiServiceRef.current == null
					) {
						return;
					}

					nmiServiceRef.current.createSecureCardFields(
						cardNumberContainerId,
						expContainerId,
						securityCodeContainerId,
						{
							textColor: colors.values.textPrimary,
							placeholderColor: hostedFieldPlaceholderColor,
						},
					);
				} else if (processorType === 'adyen') {
					if (adyenServiceRef.current == null) {
						adyenServiceRef.current = new AdyenFrontendService();
					}

					await adyenServiceRef.current.initialize('CARD', gatewayConfig);

					if (currentAbortCounter !== abortCounterRef.current) {
						return;
					}

					const cardContainer = document.getElementById(cardNumberContainerId);
					const cvvContainer = document.getElementById(securityCodeContainerId);
					const expContainerId = 'adyen-expiration';
					const expContainer = document.getElementById(expContainerId);

					if (
						cardContainer == null ||
						cvvContainer == null ||
						expContainer == null
					) {
						return;
					}

					adyenServiceRef.current.createSecureCardFields(
						cardNumberContainerId,
						expContainerId,
						securityCodeContainerId,
						{
							textColor: colors.values.textPrimary,
							placeholderColor: hostedFieldPlaceholderColor,
							// PD-16381 — the brand lights its mark in the strip; the
							// BIN drives the credit/debit lookup below.
							onCardBrandChange: setDetectedCardBrand,
							onCardBinChange: setCardBin,
						},
					);
				} else {
					return;
				}

				setSecureCardFieldsCreated(true);
			} catch (err) {
				if (currentAbortCounter === abortCounterRef.current) {
					console.error('Failed to create secure card fields:', err);
					setError(
						err instanceof Error
							? err.message
							: t('Failed to initialize secure payment fields'),
					);
				}
			} finally {
				cardInitializingRef.current = false;
			}
		},
		[
			processorType,
			secureCardFieldsCreated,
			gatewayConfig,
			t,
			colors,
			hostedFieldPlaceholderColor,
		],
	);

	// Callback when secure ACH field containers are ready.
	// nameContainerId is only provided for NMI — Collect.JS requires the
	// name-on-account (checkname) to be a hosted field for ACH tokenization.
	const handleSecureACHFieldsReady = useCallback(
		async (
			routingNumberContainerId: string,
			accountNumberContainerId: string,
			nameContainerId?: string,
		): Promise<void> => {
			if (
				secureACHFieldsCreated ||
				achInitializingRef.current ||
				gatewayConfig == null
			) {
				return;
			}

			const currentAbortCounter = abortCounterRef.current;
			achInitializingRef.current = true;

			try {
				if (processorType === 'cybersource') {
					if (cyberSourceServiceRef.current == null) {
						cyberSourceServiceRef.current = new CyberSourceFrontendService();
					}

					await cyberSourceServiceRef.current.initialize(
						'CHECK',
						gatewayConfig,
					);

					if (currentAbortCounter !== abortCounterRef.current) {
						return;
					}

					const routingContainer = document.getElementById(
						routingNumberContainerId,
					);
					const accountContainer = document.getElementById(
						accountNumberContainerId,
					);

					if (
						routingContainer == null ||
						accountContainer == null ||
						cyberSourceServiceRef.current == null
					) {
						return;
					}

					cyberSourceServiceRef.current.createSecureCheckFields(
						routingNumberContainerId,
						accountNumberContainerId,
						{
							textColor: colors.values.textPrimary,
							placeholderColor: hostedFieldPlaceholderColor,
						},
					);
				} else if (processorType === 'nmi') {
					if (nmiServiceRef.current == null) {
						nmiServiceRef.current = new NmiFrontendService();
					}

					await nmiServiceRef.current.initialize('CHECK', gatewayConfig);

					if (currentAbortCounter !== abortCounterRef.current) {
						return;
					}

					const routingContainer = document.getElementById(
						routingNumberContainerId,
					);
					const accountContainer = document.getElementById(
						accountNumberContainerId,
					);
					const nameContainer =
						nameContainerId != null
							? document.getElementById(nameContainerId)
							: null;

					if (
						routingContainer == null ||
						accountContainer == null ||
						nameContainerId == null ||
						nameContainer == null ||
						nmiServiceRef.current == null
					) {
						return;
					}

					nmiServiceRef.current.createSecureCheckFields(
						routingNumberContainerId,
						accountNumberContainerId,
						nameContainerId,
						{
							textColor: colors.values.textPrimary,
							placeholderColor: hostedFieldPlaceholderColor,
						},
					);
				} else {
					return;
				}

				setSecureACHFieldsCreated(true);
			} catch (err) {
				if (currentAbortCounter === abortCounterRef.current) {
					console.error('Failed to create secure ACH fields:', err);
					setError(
						err instanceof Error
							? err.message
							: t('Failed to initialize secure ACH payment fields'),
					);
				}
			} finally {
				achInitializingRef.current = false;
			}
		},
		[
			processorType,
			secureACHFieldsCreated,
			gatewayConfig,
			t,
			colors,
			hostedFieldPlaceholderColor,
		],
	);

	// ------------------------------------------------------------------
	// Tokenization helpers — one per vendor × payment type combination.
	// Each returns a PortalCreatePaymentMethodDto ready for the API.
	// ------------------------------------------------------------------

	const tokenizeCreditCardWithAuthorizeNet = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (authNetServiceRef.current == null) {
			authNetServiceRef.current = new AuthorizeNetFrontendService();
		}

		if (gatewayConfig == null) {
			throw new Error('Gateway config not loaded');
		}

		await authNetServiceRef.current.initialize(gatewayConfig);
		authNetServiceRef.current.validateFormData(formData);

		const opaqueData =
			await authNetServiceRef.current.tokenizeCardData(formData);
		const detectedSubtype = detectCreditCardSubtype(formData.cardNumber);
		const cleanCardNumber = formData.cardNumber.replace(/\s/g, '');
		const cardLastFour = cleanCardNumber.slice(-4);

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'Credit Card',
			paymentSubtype: detectedSubtype ?? undefined,
			paymentName: formData.nameOnCard,
			lastFour: cardLastFour,
			expirationDate: formData.expirationDate,
			// PD-15014 — Authorize.Net exposes no funding data server-side, so
			// the BIN read off the card number here (this gateway's form holds
			// the PAN pre-tokenisation) is the only classification path.
			cardBin: cleanCardNumber.slice(0, 6),
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	// PD-15708 Q9 — the mock merchant partner: behaviour from the published
	// mock card table; only the last four and BIN travel in the token.
	const tokenizeCreditCardWithMock = (
		formData: PaymentMethodFormData,
	): PortalCreatePaymentMethodDto => {
		const cleanCardNumber = formData.cardNumber.replace(/\s/g, '');
		const opaqueData = encodeMockCardToken(
			cleanCardNumber,
			formData.expirationDate,
		);
		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'Credit Card',
			paymentSubtype: detectCreditCardSubtype(cleanCardNumber) ?? undefined,
			paymentName: formData.nameOnCard,
			lastFour: cleanCardNumber.slice(-4),
			expirationDate: formData.expirationDate,
			cardBin: cleanCardNumber.slice(0, 6),
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeACHWithMock = (
		formData: PaymentMethodFormData,
	): PortalCreatePaymentMethodDto => {
		const opaqueData = encodeMockBankToken(
			formData.routingNumber,
			formData.accountNumber,
			formData.accountType,
		);
		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'ACH',
			paymentName: formData.nameOnAccount,
			lastFour: formData.accountNumber.trim().slice(-4),
			nameOnAccount: formData.nameOnAccount,
			accountType: formData.accountType,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeCreditCardWithNmi = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (nmiServiceRef.current == null) {
			throw new Error('NMI service not initialized');
		}

		nmiServiceRef.current.validateFormData(formData);

		const opaqueData = await nmiServiceRef.current.tokenizeCardData(formData);

		const tokenResponse = nmiServiceRef.current.getLastTokenResponse();
		const detectedBrand = nmiServiceRef.current.getDetectedCardBrand();
		const detectedSubtype =
			detectedBrand != null
				? (Object.values(PaymentMethodSubtypeEnum) as Array<string>).includes(
						detectedBrand,
					)
					? (detectedBrand as PaymentMethodSubtypeEnum)
					: undefined
				: undefined;

		const maskedNumber = tokenResponse?.card?.number ?? '';
		const cardLastFour = maskedNumber.slice(-4);
		const expFromToken = tokenResponse?.card?.exp;

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'Credit Card',
			paymentSubtype: detectedSubtype,
			paymentName: formData.nameOnCard,
			lastFour: cardLastFour !== '' ? cardLastFour : undefined,
			expirationDate: expFromToken ?? formData.expirationDate,
			// PD-15014 — Collect.js reports the funding type and BIN at
			// tokenisation; this flow used to drop them, which left every
			// portal-saved NMI card unclassified (the CRM panel forwarded the
			// category all along). Only a credit card may be surcharged.
			cardCategory: tokenResponse?.card?.category,
			cardBin: tokenResponse?.card?.bin,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeCreditCardWithCyberSource = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (cyberSourceServiceRef.current == null) {
			throw new Error('CyberSource service not initialized');
		}

		cyberSourceServiceRef.current.validateFormData(formData);

		const opaqueData =
			await cyberSourceServiceRef.current.tokenizeCardData(formData);

		const detectedBrand = cyberSourceServiceRef.current.getDetectedCardBrand();
		const detectedSubtype =
			detectedBrand != null
				? (Object.values(PaymentMethodSubtypeEnum) as Array<string>).includes(
						detectedBrand,
					)
					? (detectedBrand as PaymentMethodSubtypeEnum)
					: undefined
				: undefined;

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'Credit Card',
			paymentSubtype: detectedSubtype,
			paymentName: formData.nameOnCard,
			expirationDate: formData.expirationDate,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeACHWithAuthorizeNet = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (authNetServiceRef.current == null) {
			authNetServiceRef.current = new AuthorizeNetFrontendService();
		}

		if (gatewayConfig == null) {
			throw new Error('Gateway config not loaded');
		}

		await authNetServiceRef.current.initialize(gatewayConfig);
		authNetServiceRef.current.validateFormData(formData);

		const opaqueData =
			await authNetServiceRef.current.tokenizeBankData(formData);
		const accountLastFour = formData.accountNumber.trim().slice(-4);

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'ACH',
			paymentName: formData.nameOnAccount,
			lastFour: accountLastFour,
			nameOnAccount: formData.nameOnAccount,
			accountType: formData.accountType,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeACHWithNmi = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (nmiServiceRef.current == null) {
			throw new Error('NMI service not initialized');
		}

		nmiServiceRef.current.validateFormData(formData);

		const opaqueData = await nmiServiceRef.current.tokenizeBankData(formData);

		// The name on account comes from the hosted checkname field, so the
		// token response — not the (unused for NMI) formData field — is the
		// source of truth for it.
		const tokenResponse = nmiServiceRef.current.getLastTokenResponse();
		const maskedAccount = tokenResponse?.check?.account ?? '';
		const accountLastFour = maskedAccount.slice(-4);
		const nameOnAccount = tokenResponse?.check?.name ?? formData.nameOnAccount;

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'ACH',
			paymentName: nameOnAccount,
			lastFour: accountLastFour !== '' ? accountLastFour : undefined,
			nameOnAccount,
			accountType: formData.accountType,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeACHWithCyberSource = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (cyberSourceServiceRef.current == null) {
			throw new Error('CyberSource service not initialized');
		}

		cyberSourceServiceRef.current.validateFormData(formData);

		const opaqueData =
			await cyberSourceServiceRef.current.tokenizeBankData(formData);

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'ACH',
			paymentName: formData.nameOnAccount,
			nameOnAccount: formData.nameOnAccount,
			accountType: formData.accountType,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeCreditCardWithAdyen = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (adyenServiceRef.current == null) {
			throw new Error('Adyen service not initialized');
		}

		adyenServiceRef.current.validateFormData(formData);

		const opaqueData = await adyenServiceRef.current.tokenizeCardData(formData);

		const detectedBrand = adyenServiceRef.current.getDetectedCardBrand();
		const detectedSubtype =
			detectedBrand != null
				? (Object.values(PaymentMethodSubtypeEnum) as Array<string>).includes(
						detectedBrand,
					)
					? (detectedBrand as PaymentMethodSubtypeEnum)
					: undefined
				: undefined;

		const cardLastFour = adyenServiceRef.current.getCardLastFour();

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'Credit Card',
			paymentSubtype: detectedSubtype,
			paymentName: formData.nameOnCard,
			lastFour: cardLastFour ?? undefined,
			// BIN from the secured number field, for card funding classification
			// when the tokenization response carries no card additional data.
			cardBin: adyenServiceRef.current.getCardBin() ?? undefined,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	const tokenizeACHWithAdyen = async (
		formData: PaymentMethodFormData,
	): Promise<PortalCreatePaymentMethodDto> => {
		if (adyenServiceRef.current == null) {
			adyenServiceRef.current = new AdyenFrontendService();
		}

		if (gatewayConfig == null) {
			throw new Error('Gateway config not loaded');
		}

		await adyenServiceRef.current.initialize('CHECK', gatewayConfig);
		adyenServiceRef.current.validateFormData(formData);

		const opaqueData = await adyenServiceRef.current.tokenizeBankData(formData);
		const accountLastFour = formData.accountNumber.trim().slice(-4);

		return {
			dataDescriptor: opaqueData.dataDescriptor,
			dataValue: opaqueData.dataValue,
			paymentType: 'ACH',
			paymentName: formData.nameOnAccount,
			lastFour: accountLastFour !== '' ? accountLastFour : undefined,
			nameOnAccount: formData.nameOnAccount,
			accountType: formData.accountType,
			paymentAddress1: formData.addressLine1,
			paymentAddress2:
				formData.addressLine2 !== '' ? formData.addressLine2 : undefined,
			paymentCity: formData.city,
			paymentState: formData.state,
			paymentZip: formData.zipCode,
		};
	};

	// ------------------------------------------------------------------
	// Form submission — tokenize then POST to portal API
	// ------------------------------------------------------------------

	const handleFormSubmit = async (
		formData: PaymentMethodFormData,
	): Promise<boolean> => {
		if (token == null) {
			throw new Error('Not authenticated');
		}

		setError(null);

		let dto: PortalCreatePaymentMethodDto;

		if (formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD) {
			if (processorType === 'cybersource') {
				dto = await tokenizeCreditCardWithCyberSource(formData);
			} else if (processorType === 'nmi') {
				dto = await tokenizeCreditCardWithNmi(formData);
			} else if (processorType === 'adyen') {
				dto = await tokenizeCreditCardWithAdyen(formData);
			} else if (processorType === 'mock') {
				dto = tokenizeCreditCardWithMock(formData);
			} else {
				dto = await tokenizeCreditCardWithAuthorizeNet(formData);
			}
		} else if (formData.paymentType === PaymentMethodTypeEnum.ACH) {
			if (processorType === 'cybersource') {
				dto = await tokenizeACHWithCyberSource(formData);
			} else if (processorType === 'nmi') {
				dto = await tokenizeACHWithNmi(formData);
			} else if (processorType === 'adyen') {
				dto = await tokenizeACHWithAdyen(formData);
			} else if (processorType === 'mock') {
				dto = tokenizeACHWithMock(formData);
			} else {
				dto = await tokenizeACHWithAuthorizeNet(formData);
			}
		} else {
			throw new Error('Unsupported payment method type');
		}

		const result = await portalPaymentService.createPaymentMethod(token, dto);

		if (!result.success) {
			throw new Error(t('Failed to save payment method'));
		}

		onSuccess();

		return true;
	};

	// ------------------------------------------------------------------
	// Allowed payment types from supported methods
	// ------------------------------------------------------------------

	const getAllowedPaymentTypes = (): Array<PaymentMethodTypeEnum> => {
		if (
			supportedPaymentMethods == null ||
			supportedPaymentMethods.length === 0
		) {
			return [PaymentMethodTypeEnum.CREDIT_CARD];
		}

		const types: Array<PaymentMethodTypeEnum> = [];

		for (const method of supportedPaymentMethods) {
			if (
				method.paymentMethodType === 'Credit Card' &&
				!types.includes(PaymentMethodTypeEnum.CREDIT_CARD)
			) {
				types.push(PaymentMethodTypeEnum.CREDIT_CARD);
			}

			if (
				method.paymentMethodType === 'ACH' &&
				!types.includes(PaymentMethodTypeEnum.ACH)
			) {
				types.push(PaymentMethodTypeEnum.ACH);
			}
		}

		if (types.length === 0) {
			types.push(PaymentMethodTypeEnum.CREDIT_CARD);
		}

		return types;
	};

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (error != null && gatewayConfig == null) {
		// Fatal config error — can't render the form at all
		return (
			<div className="p-4">
				<p className="text-sm text-danger mb-4">{error}</p>
				<button
					type="button"
					onClick={onCancel}
					className="text-sm text-portal-accent hover:underline"
				>
					{t('Go back')}
				</button>
			</div>
		);
	}

	// CyberSource deferred — capture-context endpoint not yet available for portal
	if (processorType === 'cybersource') {
		return (
			<div className="p-4">
				<p className="text-sm text-brand-muted mb-4">
					{t(
						'Online payment method management is not yet available for your payment processor. Please contact support to add a payment method.',
					)}
				</p>
				<button
					type="button"
					onClick={onCancel}
					className="text-sm text-portal-accent hover:underline"
				>
					{t('Go back')}
				</button>
			</div>
		);
	}

	const allowedTypes = getAllowedPaymentTypes();

	return (
		<div>
			{error != null && (
				<div className="mb-4 p-3 rounded-md bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{error}</p>
				</div>
			)}

			<PaymentMethodForm
				mode="create"
				supportedPaymentMethods={supportedPaymentMethods}
				onSubmit={handleFormSubmit}
				onCancel={onCancel}
				// Rebrand the shared form's CRM-blue submit to the tenant
				// accent. The dark:-scoped classes are required — without an
				// incoming dark:bg-* counterpart, tailwind-merge keeps the
				// variant's dark:bg-blue-500 and it wins in dark mode.
				submitButtonClassName="bg-portal-accent dark:bg-portal-accent hover:bg-portal-accent-hover dark:hover:bg-portal-accent-hover rounded-lg"
				// PD-15014 — the outline variant's neutral CRM tokens read as
				// washed-out gray on the brand panel; restyle to the same brand
				// border/text vocabulary as the form's other controls.
				cancelButtonClassName="rounded-lg border-brand-border text-gray-900 dark:text-gray-100 hover:bg-brand-surface dark:hover:bg-brand-surface"
				showTypeSelector={allowedTypes.length > 1}
				allowedPaymentTypes={allowedTypes}
				preSelectedPaymentType={preSelectedPaymentType}
				processorType={processorType ?? undefined}
				// PD-15878 — the marks this tenant's processor actually accepts.
				acceptedCardBrands={gatewayConfig?.acceptedCardBrands ?? null}
				// PD-16381 — Adyen only: the other processors' fields report
				// neither, so both stay null there.
				detectedCardBrand={detectedCardBrand}
				detectedCardFunding={cardFunding}
				defaultEmail={profile?.email ?? undefined}
				onPaymentTypeChange={handlePaymentTypeChange}
				onSecureFieldsReady={(
					cardContainerId: string,
					cvvContainerId: string,
				): void => {
					void handleSecureFieldsReady(cardContainerId, cvvContainerId);
				}}
				onSecureACHFieldsReady={(
					routingContainerId: string,
					accountContainerId: string,
					nameContainerId?: string,
				): void => {
					void handleSecureACHFieldsReady(
						routingContainerId,
						accountContainerId,
						nameContainerId,
					);
				}}
			/>
		</div>
	);
};
